import React from 'react';
import { ShieldCheck, CheckCircle2, QrCode, Building, Sparkles, FileText } from 'lucide-react';
import { CommonFeeBill } from '../types';
import { thaiBahtText } from '../utils/thaiBahtText';

interface Props {
  bill: CommonFeeBill;
  receiptRef?: React.RefObject<HTMLDivElement | null>;
}

export const ReceiptDocument: React.FC<Props> = ({ bill, receiptRef }) => {
  const receiptNum = bill.receipt_number || `RC-256908-${bill.house_number.replace(/[^0-9]/g, '') || '88'}-4821`;
  const issuedDate = bill.receipt_issued_at ? new Date(bill.receipt_issued_at) : new Date();
  const paidDate = bill.paid_at ? new Date(bill.paid_at) : new Date();
  const amount = bill.amount || 1500;
  const bahtTextStr = thaiBahtText(amount);

  // Breakdown simulation for common fee
  const maintenancePortion = Math.round(amount * 0.7);
  const securityPortion = amount - maintenancePortion;

  return (
    <div
      ref={receiptRef}
      id={`receipt-${bill.id}`}
      className="receipt-print-area bg-white text-slate-900 font-sans p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm max-w-3xl mx-auto space-y-6 relative overflow-hidden"
      style={{ minWidth: '320px' }}
    >
      {/* Background Watermark */}
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.025] pointer-events-none select-none">
        <Building className="w-96 h-96 text-slate-900" />
      </div>

      {/* Top Header & Juristic Info */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pb-5 border-b-2 border-slate-800">
        <div className="flex items-start gap-3.5">
          <div className="w-13 h-13 rounded-2xl bg-gradient-to-br from-blue-700 to-indigo-900 text-white flex items-center justify-center shadow-md shrink-0">
            <Building className="w-7 h-7 text-amber-300" />
          </div>
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold tracking-widest text-blue-700 uppercase">
                NARAWADEE ELEGANCE
              </span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-[10px] text-slate-400 font-mono">EST. 2562</span>
            </div>
            <h1 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight">
              นิติบุคคลหมู่บ้านจัดสรร นราวดี อิลิแกนซ์
            </h1>
            <p className="text-xs text-slate-600 leading-snug">
              88 หมู่ 3 ถ.โรจนะ-อยุธยา ต.บ้านกรด อ.บางปะอิน จ.พระนครศรีอยุธยา 13160
            </p>
            <div className="flex flex-wrap gap-x-4 text-[11px] text-slate-500 pt-0.5">
              <span>เลขประจำตัวผู้เสียภาษี: <strong className="text-slate-700 font-mono">0145567001892</strong></span>
              <span>โทรศัพท์: <strong className="text-slate-700 font-mono">035-998-888</strong></span>
            </div>
          </div>
        </div>

        {/* Receipt Title & Number Badge */}
        <div className="text-left sm:text-right space-y-1 sm:shrink-0 bg-slate-50 sm:bg-transparent p-3 sm:p-0 rounded-xl sm:rounded-none w-full sm:w-auto border sm:border-0 border-slate-100">
          <div className="inline-block px-3 py-1 bg-slate-900 text-white rounded-lg text-xs font-bold tracking-wide uppercase shadow-xs">
            ใบเสร็จรับเงิน / ใบกำกับภาษีอย่างย่อ
          </div>
          <div className="text-[10px] text-slate-400 font-medium tracking-wider uppercase">
            Official E-Receipt / Tax Invoice
          </div>
          <div className="pt-1">
            <span className="text-xs text-slate-500">เลขที่ / No: </span>
            <span className="font-mono font-bold text-sm text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 inline-block">
              {receiptNum}
            </span>
          </div>
        </div>
      </div>

      {/* Payer and Receipt Meta Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50/80 p-4 rounded-xl border border-slate-200/80 text-xs">
        {/* Left: Payer Details */}
        <div className="space-y-1.5">
          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
            <FileText className="w-3 h-3 text-blue-600" /> ข้อมูลผู้ชำระเงิน (Resident Details)
          </div>
          <div className="flex items-center justify-between sm:justify-start gap-2">
            <span className="text-slate-500 w-24">ชื่อ-นามสกุล:</span>
            <span className="font-bold text-slate-900 text-sm">{bill.resident_name}</span>
          </div>
          <div className="flex items-center justify-between sm:justify-start gap-2">
            <span className="text-slate-500 w-24">บ้านเลขที่:</span>
            <span className="font-bold text-blue-800 bg-white px-2 py-0.5 rounded border border-slate-200 font-mono text-xs">
              {bill.house_number}
            </span>
            <span className="text-[11px] text-slate-400">(โครงการนราวดี อิลิแกนซ์)</span>
          </div>
          <div className="flex items-center justify-between sm:justify-start gap-2">
            <span className="text-slate-500 w-24">สถานะการชำระ:</span>
            <span className="inline-flex items-center gap-1 font-semibold text-emerald-700 bg-emerald-100/90 px-2 py-0.5 rounded-full text-[11px]">
              <CheckCircle2 className="w-3 h-3" /> ชำระเงินเรียบร้อยแล้ว (PAID)
            </span>
          </div>
        </div>

        {/* Right: Payment Meta Details */}
        <div className="space-y-1.5 md:border-l md:border-slate-200 md:pl-4">
          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-amber-500" /> รายละเอียดการออกเอกสาร
          </div>
          <div className="flex items-center justify-between sm:justify-start gap-2">
            <span className="text-slate-500 w-28">วันที่ออกใบเสร็จ:</span>
            <span className="font-semibold text-slate-800">
              {issuedDate.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
          <div className="flex items-center justify-between sm:justify-start gap-2">
            <span className="text-slate-500 w-28">วันเวลาที่โอนเงิน:</span>
            <span className="font-medium text-slate-700">
              {paidDate.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })} • {paidDate.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.
            </span>
          </div>
          <div className="flex items-center justify-between sm:justify-start gap-2">
            <span className="text-slate-500 w-28">ช่องทางชำระเงิน:</span>
            <span className="font-medium text-slate-800">
              {bill.payment_method || 'Mobile Banking (PromptPay QR)'}
            </span>
          </div>
        </div>
      </div>

      {/* Itemized Table */}
      <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        <table className="w-full text-xs">
          <thead className="bg-slate-900 text-white font-semibold">
            <tr>
              <th className="py-2.5 px-3.5 text-center w-12">ลำดับ</th>
              <th className="py-2.5 px-3.5 text-left">รายการชำระ (Description)</th>
              <th className="py-2.5 px-3.5 text-center w-24">งวดประจำ</th>
              <th className="py-2.5 px-3.5 text-right w-28">จำนวนเงิน (บาท)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            <tr>
              <td className="py-3 px-3.5 text-center font-mono text-slate-400">01</td>
              <td className="py-3 px-3.5">
                <p className="font-bold text-slate-900">
                  ค่าบำรุงรักษาและการจัดการสาธารณูปโภคส่วนกลาง
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  • ไฟฟ้าส่องสว่างทางเท้า, ดูแลสวนหย่อม, สระว่ายน้ำ, คลับเฮาส์ และการจัดเก็บขยะมูลฝอย
                </p>
              </td>
              <td className="py-3 px-3.5 text-center font-medium text-slate-700">{bill.period}</td>
              <td className="py-3 px-3.5 text-right font-mono font-bold text-slate-800">
                {maintenancePortion.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
            </tr>
            <tr>
              <td className="py-3 px-3.5 text-center font-mono text-slate-400">02</td>
              <td className="py-3 px-3.5">
                <p className="font-bold text-slate-900">
                  ค่าบริการระบบรักษาความปลอดภัย 24 ชั่วโมง และระบบสายตรวจ QR Patrol
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  • เจ้าหน้าที่ รปภ. ประจำป้อมทางเข้า-ออก, กล้องวงจรปิด CCTV และระบบคัดกรองบุคคลภายนอก
                </p>
              </td>
              <td className="py-3 px-3.5 text-center font-medium text-slate-700">{bill.period}</td>
              <td className="py-3 px-3.5 text-right font-mono font-bold text-slate-800">
                {securityPortion.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
            </tr>
          </tbody>
          <tfoot className="bg-slate-50 border-t border-slate-200">
            <tr>
              <td colSpan={2} rowSpan={3} className="p-3.5 align-middle border-r border-slate-200">
                <div className="space-y-1">
                  <span className="text-[11px] font-bold text-slate-500 block">
                    จำนวนเงินตัวอักษร (Amount in Words):
                  </span>
                  <p className="font-bold text-blue-900 text-xs sm:text-sm bg-blue-50/70 p-2 rounded-lg border border-blue-100">
                    ({bahtTextStr})
                  </p>
                </div>
              </td>
              <td className="py-2 px-3 text-right font-medium text-slate-500">รวมเป็นเงิน:</td>
              <td className="py-2 px-3.5 text-right font-mono font-bold text-slate-800">
                {amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
            </tr>
            <tr>
              <td className="py-2 px-3 text-right font-medium text-slate-500">
                ภาษีมูลค่าเพิ่ม (VAT 7%):
              </td>
              <td className="py-2 px-3.5 text-right font-mono text-slate-500">
                0.00*
              </td>
            </tr>
            <tr className="bg-blue-50/80 border-t border-blue-200">
              <td className="py-3 px-3 text-right font-bold text-slate-900 text-sm">
                ยอดสุทธิทั้งสิ้น:
              </td>
              <td className="py-3 px-3.5 text-right font-mono font-extrabold text-blue-800 text-base">
                ฿{amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="text-[10px] text-slate-400 italic">
        *หมายเหตุ: ค่าส่วนกลางหมู่บ้านจัดสรรได้รับการยกเว้นภาษีมูลค่าเพิ่มตามประมวลรัษฎากรและ พ.ร.บ. จัดสรรที่ดิน พ.ศ. 2543
      </p>

      {/* Authentication, Digital Seal & Signatures */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t-2 border-slate-200 text-xs items-center">
        {/* Verification QR */}
        <div className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 border border-slate-200">
          <div className="p-1 bg-white border border-slate-200 rounded-lg shrink-0">
            <QrCode className="w-12 h-12 text-slate-900" />
          </div>
          <div>
            <div className="flex items-center gap-1 text-[11px] font-bold text-emerald-700">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> ตรวจสอบสลิปถูกต้อง
            </div>
            <p className="text-[10px] text-slate-500 font-mono mt-0.5">
              Ref: {receiptNum.slice(0, 16)}
            </p>
            <p className="text-[9px] text-slate-400">สแกนเพื่อตรวจสอบความถูกต้อง</p>
          </div>
        </div>

        {/* Digital Juristic Stamp */}
        <div className="text-center p-2">
          <div className="inline-block border-2 border-dashed border-blue-400/80 rounded-2xl p-2 bg-blue-50/40 text-blue-900">
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white mx-auto flex items-center justify-center mb-1">
              <Building className="w-4 h-4" />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-blue-800">
              ตราประทับดิจิทัล
            </p>
            <p className="text-[9px] text-blue-600">นิติบุคคล นราวดี อิลิแกนซ์</p>
          </div>
        </div>

        {/* Authorized Issuer Signature */}
        <div className="text-center sm:text-right space-y-1">
          <div className="inline-block text-center space-y-1">
            <div className="h-9 flex items-end justify-center">
              <span className="font-serif italic font-bold text-blue-900 text-base opacity-90 tracking-wide border-b border-slate-400 pb-0.5 px-4">
                {bill.receipt_issued_by || bill.verified_by || 'ศศิธร สุขสมบูรณ์'}
              </span>
            </div>
            <p className="font-bold text-slate-800 text-xs">
              ({bill.receipt_issued_by || bill.verified_by || 'คุณศศิธร สุขสมบูรณ์'})
            </p>
            <p className="text-[10px] text-slate-500">
              ผู้จัดการนิติบุคคล / ฝ่ายการเงินโครงการ
            </p>
          </div>
        </div>
      </div>

      {/* Footer Legal Subtext */}
      <div className="text-center text-[10px] text-slate-400 pt-3 border-t border-slate-100">
        เอกสารฉบับนี้จัดทำขึ้นโดยระบบบริหารจัดการนิติบุคคลหมู่บ้านจัดสรร นราวดี อิลิแกนซ์ มีผลสมบูรณ์ตามพระราชบัญญัติว่าด้วยธุรกรรมทางอิเล็กทรอนิกส์ พ.ศ. 2544
      </div>
    </div>
  );
};
